import { autoinject, transient } from "aurelia-dependency-injection";
import { EventAggregator } from "aurelia-event-aggregator";
import { Messages } from "../../../configuration/event-messages.constants";
import { ArrayUtils } from "../../../utilities/arrays";
import { EntityValidationsService } from "../../../client-validation-system/validations-service";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { OnboardingClientService } from "../../../services/onboarding/onboarding-client.service";
import { computedFrom, observable } from "aurelia-binding";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { CompDialog } from "@rightship-tech/common-ui/components/comp-dialog/comp-dialog";
import * as Enumerable from "linq";
import { UrlHelper } from "../../../helpers/url-helper";
import { Guid } from "../../../utilities/guid";
import { Router } from "aurelia-router";
import { DialogServiceConstants } from "../../../constants/basics/dialog.constants";
import { Reflection } from "../../../utilities/reflection";
import { IOnboardingSessionHandler, OnboardingNavigationStepModel, OnboardingStepModel } from "../../pages/onboarding-client/interfaces/contracts-handlers.models";
import { InvalidCountry } from "../../pages/onboarding-client/steps/invalid-country/invalid-country";
import { OnboardingSession } from "../../pages/onboarding-client/onboarding.session";
import { BaseOnboardingStepModel } from "../../pages/onboarding-client/steps/base-step-model";
import { RightshipOnboardingValidationRenderer } from "../../pages/onboarding-client/rightship-onboarding-validation-renderer";
import { DynamicImportService } from "../../../services/dynamic-import/dynamic-import-service";
import { OnboardingNavigationHandlers } from "../../pages/onboarding-client/onboarding-navigation-handlers";
import { Container } from "aurelia-framework";

//Load styles
require("../../../styles/onboarding-client/core-onboarding-client.scss");

@autoinject()
@transient()
export class LayoutOnboarding implements IOnboardingSessionHandler {

    private _invalidCountry: InvalidCountry;
    private _onboardingSession: OnboardingSession;
    private _step: BaseOnboardingStepModel;
    private _lastStep: BaseOnboardingStepModel;

    public isNext: boolean = true;
    public showDatePicker: boolean = false;
    public masterPage: string = "";
    public model: { step: BaseOnboardingStepModel, isNext: boolean, key: string, steps: OnboardingStepModel[] } = { isNext: false, step: null, key: null, steps: [] };
    public stepIndex: number;
    public steps: OnboardingStepModel[] = [];
    public visibleSteps: OnboardingStepModel[] = [];
    public isEmbedded: boolean;

    @computedFrom(Reflection.getPropertyPath<LayoutOnboarding>(x => x._step))
    get currentStep(): BaseOnboardingStepModel {
        return this._step;
    }

    @observable()
    dialogHostVM: CompDialog;

    constructor(private onboardingClientService: OnboardingClientService,
        private lookupClientService: LookupClientService,
        private dialogClientService: DialogClientService,
        private signalClientService: SignalClientService,
        private dynamicImportService: DynamicImportService,
        private router: Router,
        private container: Container,
        private entityValidationsService: EntityValidationsService,
        private eventAggregator: EventAggregator) {

        this.entityValidationsService.changeValidationRenderer(new RightshipOnboardingValidationRenderer());
        this._onboardingSession = new OnboardingSession(onboardingClientService);
        container.registerInstance(OnboardingSession, this._onboardingSession);
        this._invalidCountry = new InvalidCountry(onboardingClientService, this._onboardingSession);
    }

    private loadSteps(): Promise<void> {
        return new Promise<void>((resolve) => {
            const navigationHandlerKey = this._onboardingSession.navigationHandler || "default";
            const navigationHandler = OnboardingNavigationHandlers.filter(x => x.key == navigationHandlerKey)[0];
            this.loadNavigation(navigationHandler.navigation).then(steps => {
                this.steps = Enumerable.from(steps).orderBy(x => x.order).toArray();
                this.steps[0].isActive = true;
                resolve();
            });
        });
    }

    private loadNavigation(navigation: OnboardingNavigationStepModel[]): Promise<OnboardingStepModel[]> {
        return new Promise<OnboardingStepModel[]>((resolve) => {
            const steps: OnboardingStepModel[] = [];
            let promises = [];
            navigation.forEach((item, index) => {
                promises.push(this.dynamicImportService.getInstance(item.path(), this.container).then((instance: BaseOnboardingStepModel) => {
                    steps.push({
                        isActive: false,
                        isComplete: false,
                        order: index,
                        name: item.name,
                        key: item.key,
                        vm: instance,
                        navigationPaths: item.navigationPaths || {}
                    });
                }));
            });
            Promise.all(promises).then(() => {
                promises = [];
                steps.forEach(x => promises.push(x.vm.prepare()));
                Promise.all(promises).then(() => {
                    resolve(Enumerable.from(steps).orderBy(x => x.order).toArray());
                });
            });
        });
    }

    continueFromMobile() {
        this.dialogClientService.show(DialogServiceConstants.URLs.ContinueFromMobileDialog, {});
    }

    continuingFromMobile = () => {
        this.dialogClientService.hide();
        this.dialogClientService.show(DialogServiceConstants.URLs.ResumedFromMobileDialog, {}, false);
    }

    activate(model: { guid: string, query: string, isEmbedded: boolean }) {
        
        // let config = _onboardingService.GetOnboardingConfigByLandingPageGuid(guid);

        this.isEmbedded = model ? model.isEmbedded : false;
        if (!this.isEmbedded) {
            this.eventAggregator.subscribe(Messages.showDatePicker, () => {
                this.showDatePicker = true;
            });

            this.eventAggregator.subscribe(Messages.hideDatePicker, () => {
                this.showDatePicker = false;
            });

            this.lookupClientService.clearAllLookups();
        }

        if (!window["onboardingRequestUrlMeta"]) {
            window["onboardingRequestUrlMeta"] = {};
        }

        if (!window["onboardingRequestUrlMeta"].uniqueIdentifier) {
            window["onboardingRequestUrlMeta"].uniqueIdentifier = "onboarding-session-" + Guid.generateGuid();
        }

        this.signalClientService.subscribe('continuingFromMobile', this.continuingFromMobile);
        return new Promise<void>((resolve) => {

            this._onboardingSession.createSession(this, model).then(() => {
                this.loadSteps().then(() => {
                    let landingPage = this._onboardingSession.landingPage;
                    landingPage = landingPage ? landingPage : 'default';
                    this.loadCss(this._onboardingSession.pluginName, landingPage);

                    require('../../plugins/onboarding-requests/'+ this._onboardingSession.pluginName +'/landing-pages/' + landingPage + '/master-page.ts');
                    require('../../plugins/onboarding-requests/'+ this._onboardingSession.pluginName +'/landing-pages/' + landingPage + '/master-page.html');
                    this.masterPage = `../../plugins/onboarding-requests/${this._onboardingSession.pluginName}/landing-pages/${landingPage}/master-page`;
                    this.visibleSteps = this.steps.filter(x => x.vm.canShow);
                    setTimeout(() => {
                        const onboardingRequestGuid = UrlHelper.getParameterValueByKey('onboardingRequestGuid', this._onboardingSession.query);
                        let lastStep = Number(UrlHelper.getParameterValueByKey('lastStep', this._onboardingSession.query));

                        if (onboardingRequestGuid) {
                            this._onboardingSession.updateSession(onboardingRequestGuid);
                            this.onboardingClientService.getOnboardingRequest().then(response => {
                                const object = UrlHelper.getQueryStringAsObject();
                                window["onboardingRequestUrlMeta"].fromUniqueIdentifier = object.uniqueIdentifier;
                                window["onboardingRequestUrlMeta"].lastMetaPageNumber = object.currentMetaPageNumber ? Number(object.currentMetaPageNumber) : 0;
                                window["onboardingRequestUrlMeta"].lastVerificationNumber = object.currentVerificationNumber ? Number(object.currentVerificationNumber) : 0;
                                delete object.uniqueIdentifier;
                                $.extend({}, window["onboardingRequestUrlMeta"], UrlHelper.getQueryStringAsObject());
                                response.onboardingRequest.verifications.forEach(verification => {
                                    const internalName = verification.onboardingVerificationPlugin.internalName;
                                    this._onboardingSession.verificationsMeta[internalName] = verification.meta ? JSON.parse(verification.meta) : null;
                                });
                                this.applyValues(this._onboardingSession.modelMeta, JSON.parse(response.onboardingRequest.modelMeta) || {});
                                this._onboardingSession.storeDocuments(response.onboardingRequest.uploadedDocuments);
                                this._onboardingSession.setOfferingsSelected(response.onboardingRequest.offeringsSelected.map(x => x.offering));
                                this.isNext = true;

                                //If Onboarding Request is completed then it should automatically go to the last step
                                if (response.onboardingRequest.isCompleted || response.onboardingRequest.isRejected) {
                                    lastStep = this.visibleSteps.length - 1;

                                    //RS SPECIFIC CODE
                                    // for expired/rejected form to not sow submitted successfully message
                                    window["onboardingRequestUrlMeta"].rejectedReason = response.onboardingRequest.rejectionReason;
                                    window["onboardingRequestUrlMeta"].isRejected = response.onboardingRequest.isRejected;
                                    window["onboardingRequestUrlMeta"].rejectedTitle = response.onboardingRequest && response.onboardingRequest.rejectionReason
                                        && response.onboardingRequest.rejectionReason.toLowerCase().trim() == "survey link expired" ? "Expired" : "This form is no longer available";
                                }

                                if (response.onboardingRequest.urlsHistoryJson && !response.onboardingRequest.isCompleted) {
                                    window.history.replaceState({ path: location.origin + location.pathname }, '', location.origin + location.pathname);

                                    setTimeout(() => {

                                        const urls = JSON.parse(response.onboardingRequest.urlsHistoryJson || "[]");

                                        urls.forEach((url) => {
                                            window.history.pushState({ path: url }, '', url);
                                            this._onboardingSession.pushUrl(url);
                                        });

                                        this._onboardingSession.startListeningToHistoryChanges();
                                        this._lastStep = lastStep > 0 ? this.visibleSteps[lastStep - 1].vm : null;
                                        this.goToStep(lastStep, null);
                                    }, 120);
                                } else {

                                    setTimeout(() => {

                                        this._onboardingSession.startListeningToHistoryChanges();
                                        this._lastStep = lastStep > 0 ? this.visibleSteps[lastStep - 1].vm : null;
                                        this.goToStep(lastStep, null);
                                    }, 120);
                                }
                            });
                        } else {
                            const object = UrlHelper.getQueryStringAsObject();
                            window["onboardingRequestUrlMeta"].fromUniqueIdentifier = object.uniqueIdentifier;
                            delete object.uniqueIdentifier;
                            $.extend({}, window["onboardingRequestUrlMeta"], UrlHelper.getQueryStringAsObject());
                            this.isNext = true;
                            this.goToStep(lastStep, null);
                            this._onboardingSession.startListeningToHistoryChanges();
                        }
                        resolve();
                    });
                })
            }).catch((e: { isValidCountry: boolean, pluginName: string, onboardingRequestsPath: string, landingPage: string, country: string }) => {
                if (e) {
                    require('../../plugins/onboarding-requests/'+ e.pluginName +'/landing-pages/' + e.landingPage + '/master-page.ts'); 
                    require('../../plugins/onboarding-requests/'+ e.pluginName +'/landing-pages/' + e.landingPage + '/master-page.html');
                    this.masterPage = `../../plugins/onboarding-requests/${e.pluginName}/landing-pages/${e.landingPage}/master-page`;
                    this._invalidCountry.country = e.country;
                    this.model = { step: this._invalidCountry, isNext: true, key: "invalid-country", steps: this.steps };
                    this.loadCss(e.pluginName, e.landingPage);
                    resolve();
                }
            });
        });
    }

    applyValues(applyToObj: any, objMapper: any) {
        Object.keys(objMapper).forEach(key => {
            applyToObj[key] = objMapper[key];
        });
    }

    attached() {
        document.addEventListener("click", this.documentClick);
    }

    detached() {
        document.removeEventListener("click", this.documentClick);
        this._onboardingSession.destroySession();
    }

    public documentClick: EventListenerOrEventListenerObject = e => {
        if (!(<any>e).propagationStopped) {
            this.eventAggregator.publish(Messages.documentClick, { source: "document" });
        }
    };

    currentStepIndex() {
        return ArrayUtils.findIndex(this.visibleSteps, x => x.vm == this._step);
    }

    countSteps() {
        return this.visibleSteps.filter(x => x.vm.canShow).length;
    }

    setStepsState() {
        const visibleSteps = this.visibleSteps;
        const i = ArrayUtils.findIndex(visibleSteps, x => x.vm == this._step);
        window["onboardingRequestUrlMeta"].lastStep = i;
        visibleSteps[i].isComplete = false;
        visibleSteps[i].isActive = true;
        visibleSteps.forEach((step, index) => {
            if (index > i) {
                step.isActive = false;
                step.isComplete = false;
            } else if (index != i) {
                step.isActive = false;
                step.isComplete = true;
            }
        });
        if (i == this.countSteps() - 1) {
            visibleSteps[i].isActive = false;
            visibleSteps[i].isComplete = true;
        }
        this.model = { step: this._step, isNext: this.isNext, key: visibleSteps[i].key, steps: this.steps };
        this.stepIndex = i + 1;
    }

    goToStep(index: number, navigationPathKey: string): Promise<void> {
        return new Promise<void>((resolve) => {
            const visibleSteps = this.visibleSteps;

            const lastStepIndex = this._lastStep ? ArrayUtils.findIndex(visibleSteps, x => x.vm == this._lastStep) : 0;

            const setStep = () => {
                if (index <= lastStepIndex + 1 && visibleSteps.length >= index + 1) {
                    this.isNext = ArrayUtils.findIndex(visibleSteps, x => x.vm == this._step) <= index;
                    if (index >= 0) {
                        this._step = visibleSteps[index].vm;
                        this._lastStep = !this._lastStep || index > ArrayUtils.findIndex(visibleSteps, x => x.vm == this._lastStep) ? this._step : this._lastStep;
                        this.setStepsState();
                        if (window["onboardingRequestUrlMeta"].fromUniqueIdentifier) {
                            this.signalClientService.notifyServer('OnboardingHub', 'startedFromMobile', { toUniqueIdentifier: window["onboardingRequestUrlMeta"].fromUniqueIdentifier });
                            delete window["onboardingRequestUrlMeta"].fromUniqueIdentifier;
                            this.dialogClientService.show(DialogServiceConstants.URLs.ProceedWithOnboardingFromMobile, {});
                        }
                    } else {
                        this.router.navigateBack();
                    }
                }
            }

            if (navigationPathKey) {
                const iStep = ArrayUtils.findIndex(this.steps, x => x.vm == this._step);
                this.steps.splice(iStep + 1, this.steps.length - iStep);
                const navigationPaths = visibleSteps[this.currentStepIndex()].navigationPaths[navigationPathKey];
                this.loadNavigation(navigationPaths).then(newSteps => {
                    this.steps.push(...newSteps);
                    this.visibleSteps = this.steps.filter(x => x.vm.canShow);
                    setStep();
                    resolve();
                });
            } else {
                setStep();

            }

            resolve();
        });
    }

    private loadCss(pluginName: string, landingPage: string) {
        require(`views/plugins/onboarding-requests/${pluginName}/landing-pages/${landingPage}/styles/styles.scss`)
    }
}
